/**
 * Maps `items` with `fn`, running at most `batchSize` calls at a time.
 *
 * `Promise.all(items.map(fn))` starts every call at once, which for a per-version
 * fan-out means one concurrent request per version of the string group. This
 * walks the list in fixed-size batches instead: each batch runs in parallel and
 * the next starts only once the previous settles, so the peak in-flight count is
 * bounded no matter how long the list is. Results keep the input order.
 *
 * `fn` must resolve rather than reject — a rejection aborts the remaining
 * batches. Callers that want per-item failures should resolve to a result
 * object describing the failure (as the version history panel does).
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  batchSize: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = [];
  for (let start = 0; start < items.length; start += batchSize) {
    const batch = items.slice(start, start + batchSize);
    // Sequential batches are the point: awaiting here is what bounds concurrency.
    results.push(...(await Promise.all(batch.map(fn))));
  }
  return results;
}
